/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * @file AssignmentQuestionCard.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import Icon from '../icons/Icon';
import MathTextComponent from '../common/MathTextComponent';
import { getOptionLetter } from '../questionPreview/questionPreviewUtils';
import { AssignmentQuestionCardStylesRoot, classes } from './styles/QuestionTopicsModalStyles';

/**
 * Questions & Topics modal card. The "Question N of M" header toggles the answer options;
 * the stem stays visible while collapsed, and the correct option is highlighted green.
 */
const AssignmentQuestionCard = ({
  questionLabel,
  questionStem,
  options,
  defaultExpanded
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const optionsId = useId();

  return (
    <AssignmentQuestionCardStylesRoot className={classes.questionCard}>
      <button
        type="button"
        className={classes.questionCardHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-controls={optionsId}
      >
        <Typography component="p" className={classes.questionCardTitle}>
          {questionLabel}
        </Typography>
        <span
          className={`${classes.expandIcon} ${isExpanded ? classes.expandIconExpanded : ''}`}
          aria-hidden="true"
        >
          <Icon name="ExpandIcon" />
        </span>
      </button>
      <div className={classes.questionCardBody}>
        <MathTextComponent
          text={questionStem}
          className={classes.questionStem}
          component="p"
        />

        {isExpanded && options.length > 0 && (
          <ul id={optionsId} className={classes.optionsList}>
            {options.map((option, index) => {
              const isCorrect = Boolean(option.correct);

              return (
                <li
                  key={option.id ?? index}
                  className={`${classes.optionRow} ${isCorrect ? classes.optionRowCorrect : ''}`}
                >
                  <span className={`${classes.optionPrefix} ${isCorrect ? classes.optionPrefixCorrect : ''}`}>
                    {getOptionLetter(index)}
                  </span>
                  <MathTextComponent
                    text={option.label || ''}
                    className={`${classes.optionLabel} ${isCorrect ? classes.optionLabelCorrect : ''}`}
                  />
                  {isCorrect && (
                    <span className={classes.correctIcon} aria-hidden="true">
                      <Icon name="PositiveIcon" />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AssignmentQuestionCardStylesRoot>
  );
};

AssignmentQuestionCard.propTypes = {
  questionLabel: PropTypes.string.isRequired,
  questionStem: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    correct: PropTypes.bool
  })),
  defaultExpanded: PropTypes.bool
};

AssignmentQuestionCard.defaultProps = {
  options: [],
  defaultExpanded: false
};

export default AssignmentQuestionCard;
